import { create } from "zustand";
import {
  buildAnalyticsRange,
  validateAnalyticsRange,
} from "@/features/admin/adminAnalyticsUtils";
import {
  loadAnalyticsLiveKpis,
  loadAnalyticsSnapshot,
} from "@/features/admin/api/adminAnalyticsClient";
import type {
  AnalyticsLiveKpis,
  AnalyticsRangePreset,
  AnalyticsSnapshotResponse,
  AnalyticsTab,
  AnalyticsTimeRange,
} from "@/features/admin/adminAnalytics.types";

type LoadingState = Partial<Record<AnalyticsTab | "live", boolean>>;
type SnapshotCache = Partial<Record<AnalyticsTab, AnalyticsSnapshotResponse>>;

type AdminAnalyticsStore = {
  activeTab: AnalyticsTab;
  error: string | null;
  lastRefreshed: Date | null;
  liveKPIs: AnalyticsLiveKpis | null;
  loading: LoadingState;
  refresh: () => Promise<void>;
  setActiveTab: (tab: AnalyticsTab) => Promise<void>;
  setTimeRange: (
    preset: AnalyticsRangePreset,
    custom?: Pick<AnalyticsTimeRange, "endDate" | "startDate">,
  ) => Promise<void>;
  snapshotData: SnapshotCache;
  timeRange: AnalyticsTimeRange;
};

const initialRange = buildAnalyticsRange("last_30_days");

async function loadActiveSnapshot(state: AdminAnalyticsStore, tab = state.activeTab) {
  if (state.loading[tab]) {
    return;
  }

  useAdminAnalyticsStore.setState((current) => ({
    error: null,
    loading: {
      ...current.loading,
      [tab]: true,
    },
  }));

  try {
    const snapshot = await loadAnalyticsSnapshot(tab, state.timeRange);
    useAdminAnalyticsStore.setState((current) => ({
      lastRefreshed: new Date(),
      loading: {
        ...current.loading,
        [tab]: false,
      },
      snapshotData: {
        ...current.snapshotData,
        [tab]: snapshot,
      },
    }));
  } catch (error) {
    useAdminAnalyticsStore.setState((current) => ({
      error: error instanceof Error ? error.message : "ANALYTICS_LOAD_FAILED",
      loading: {
        ...current.loading,
        [tab]: false,
      },
    }));
  }
}

async function loadLiveKpis(state: AdminAnalyticsStore) {
  if (state.loading.live) {
    return;
  }

  useAdminAnalyticsStore.setState((current) => ({
    error: null,
    loading: {
      ...current.loading,
      live: true,
    },
  }));

  try {
    const liveKPIs = await loadAnalyticsLiveKpis();
    useAdminAnalyticsStore.setState((current) => ({
      lastRefreshed: new Date(),
      liveKPIs,
      loading: {
        ...current.loading,
        live: false,
      },
    }));
  } catch (error) {
    useAdminAnalyticsStore.setState((current) => ({
      error: error instanceof Error ? error.message : "ANALYTICS_LOAD_FAILED",
      loading: {
        ...current.loading,
        live: false,
      },
    }));
  }
}

export const useAdminAnalyticsStore = create<AdminAnalyticsStore>((set, get) => ({
  activeTab: "overview",
  error: null,
  lastRefreshed: null,
  liveKPIs: null,
  loading: {},
  refresh: async () => {
    const state = get();
    await Promise.all([loadLiveKpis(state), loadActiveSnapshot(state)]);
  },
  setActiveTab: async (tab) => {
    set({ activeTab: tab });
    const state = get();
    if (!state.snapshotData[tab]) {
      await loadActiveSnapshot(state, tab);
    }
  },
  setTimeRange: async (preset, custom) => {
    const timeRange = validateAnalyticsRange(buildAnalyticsRange(preset, new Date(), custom));
    set({
      error: null,
      snapshotData: {},
      timeRange,
    });
    await loadActiveSnapshot(get());
  },
  snapshotData: {},
  timeRange: initialRange,
}));
